import { create } from 'zustand';
import type { CqgEnvironment } from '@features/cqg/lib/cqgCreds';

/**
 * Open-state for the account-connection surfaces (2026-07 redesign). The CQG/Rithmic connector
 * dialogs MUST stay permanently mounted in the Topbar — they own the relay lifecycle + auto-resume
 * effects — so anything that wants to open them (Accounts-panel Link buttons, the Add-account
 * dialog's take-over handoff, the panel itself) goes through this store instead of prop-drilling
 * from the Topbar. Also lets `CqgReconnectPopup` suppress itself while a connect surface is open
 * (two competing take-over buttons otherwise — 2026-07-18 review).
 *
 * NOTE: `CqgConnectBanner` still mounts its OWN transient CqgConnectorDialog instance outside this
 * store (intentional — its open-state is tied to the first-time banner flow this store doesn't
 * model). `openCqg()` here only drives the Topbar instance.
 */
interface ConnectDialogState {
  /** The right-side Accounts panel (F2). */
  panelOpen: boolean;
  /** The Topbar-mounted CQG connector dialog. */
  cqgOpen: boolean;
  /** The Topbar-mounted Rithmic connect dialog. */
  rithmicOpen: boolean;
  /** The Add-account dialog (broker picker). */
  addAccountOpen: boolean;
  /**
   * One-shot hint for the CQG dialog: set when opening it from an Accounts-panel row so the dialog
   * preselects the matching Demo/Live tab AND prefills the username last used for that account
   * (2026-07-18; username comes from the FE-only per-account login memory, never the DB). The dialog
   * consumes + clears it on open (`clearCqgHint`), so a later manual open isn't sticky.
   */
  cqgHint: { environment: CqgEnvironment | null; username: string | null } | null;
  /**
   * One-shot hint for the Rithmic dialog: set when opening it from an Accounts-panel row so the
   * dialog preselects the matching system dropdown AND prefills the username last used for that
   * account (2026-07-21; username + system come from the FE-only per-account login memory, never the
   * DB). Rithmic has no demo/live environment, so it carries `system` where CQG carries
   * `environment`. The dialog consumes + clears it on open (`clearRithmicHint`).
   */
  rithmicHint: { system: string | null; username: string | null } | null;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  /** Open the Topbar CQG dialog, optionally preselecting the tab + prefilling the username. */
  openCqg: (hint?: { environment?: CqgEnvironment | null; username?: string | null }) => void;
  closeCqg: () => void;
  clearCqgHint: () => void;
  /** Open the Topbar Rithmic dialog, optionally preselecting the system + prefilling the username. */
  openRithmic: (hint?: { system?: string | null; username?: string | null }) => void;
  closeRithmic: () => void;
  clearRithmicHint: () => void;
  openAddAccount: () => void;
  closeAddAccount: () => void;
}

export const useConnectDialogStore = create<ConnectDialogState>((set) => ({
  panelOpen: false,
  cqgOpen: false,
  rithmicOpen: false,
  addAccountOpen: false,
  cqgHint: null,
  rithmicHint: null,
  openPanel: () => set({ panelOpen: true }),
  closePanel: () => set({ panelOpen: false }),
  togglePanel: () => set(s => ({ panelOpen: !s.panelOpen })),
  openCqg: (hint) => set({
    cqgOpen: true,
    cqgHint: hint ? { environment: hint.environment ?? null, username: hint.username ?? null } : null,
  }),
  closeCqg: () => set({ cqgOpen: false }),
  clearCqgHint: () => set({ cqgHint: null }),
  openRithmic: (hint) => set({
    rithmicOpen: true,
    rithmicHint: hint ? { system: hint.system ?? null, username: hint.username ?? null } : null,
  }),
  closeRithmic: () => set({ rithmicOpen: false }),
  clearRithmicHint: () => set({ rithmicHint: null }),
  openAddAccount: () => set({ addAccountOpen: true }),
  closeAddAccount: () => set({ addAccountOpen: false }),
}));

/** True while ANY connect surface that carries its own take-over UI is open — used by
 *  CqgReconnectPopup to avoid double-surfacing the concurrent-session prompt. */
export const useAnyConnectSurfaceOpen = () =>
  useConnectDialogStore(s => s.cqgOpen || s.rithmicOpen || s.addAccountOpen);
